import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Typography, CircularProgress, Alert, Paper, Divider, IconButton, Tooltip, Backdrop,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, TextField
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SyncIcon from "@mui/icons-material/Sync";
import { axios_tenant } from "../../../../axios";
import useTaskDetail from "../../../shared/OAWorkflow/hooks/useTaskDetail";
import TaskDetailPanel from "../../../shared/OAWorkflow/components/TaskDetailPanel";
import PackageSection from "../../../shared/OAWorkflow/components/PackageSection";
import ObservationTable from "../../../shared/OAWorkflow/components/ObservationTable";
import EmailDraftSection from "../../../shared/OAWorkflow/components/EmailDraftSection";
import Button from "../../../shared/OAWorkflow/components/atoms/Button";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { formatDateDDMMMYYYY } from "../../../shared/OAWorkflow/utils/dateUtils";
import { 
    isSubmitEnabled as isSubmitEnabledShared, 
    getSubmitDisabledReason as getSubmitDisabledReasonShared, 
    getSubmitConfirmMessage as getSubmitConfirmMessageShared 
} from "../../../shared/OAWorkflow/utils/taskTransitions";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

/**
 * Full task detail page with tabs for Packages, Observations, Notes, and Audit.
 * Route: /collaboration-space/oa-workflow/tasks/:taskId
 */
const OAWorkflowTaskDetail = () => {
    useDocumentTitle("OA Shell — Task Details");
    const { taskId } = useParams();
    const navigate = useNavigate();
    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    const {
        task, packages, observations,
        permittedTransitions, submitTransitions, allowedActions, canAdminAssign,
        loading, error, refetch, refetchObservations
    } = useTaskDetail(taskId);
    const [workflowUsers, setWorkflowUsers] = useState([]);
    const [userRole, setUserRole] = useState("Client");
    const [canActAs, setCanActAs] = useState([]);
    const [currentUser, setCurrentUser] = useState("");
    const [audits, setAudits] = useState([]);

    // Local assignment state for Admin "To Do" and "Assign QA" flows (not saved until Submit)
    const [localProcessorId, setLocalProcessorId] = useState(null);
    const [localQaId, setLocalQaId] = useState(null);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));
    const showSuccess = (message) => setSnackbar({ open: true, message, severity: "success" });

    // Sync local assignment state from task data on load/refetch
    React.useEffect(() => {
        if (task) {
            setLocalProcessorId(task.processorId || null);
            // COL-137: "Not Required" QA sentinel — reflect qcRequired back into
            // the dropdown so re-opening an already-Not-Required task shows it.
            setLocalQaId(task.qcRequired === false ? "NOT_REQUIRED" : (task.qaId || null));
        }
    }, [task?.processorId, task?.qaId, task?.qcRequired]);

    React.useEffect(() => {
        const fetchMeta = async () => {
            try {
                const meRes = await axios_tenant.get(`${BASE_URL}/me?workType=OA Shell`);
                setUserRole(meRes.data.role);
                setCanActAs(meRes.data.canActAs || []);
                const identity = meRes.data.role ? `${meRes.data.displayName} (${meRes.data.role})` : meRes.data.displayName;
                setCurrentUser(identity);
                console.log(`[OA Task Detail] Identity resolved: ${identity}`);

                if (meRes.data.role === "Admin" || meRes.data.role === "Application Manager") {
                    console.log(`[OA Task Detail] Fetching elevated meta for Admin/AM...`);
                    const uRes = await axios_tenant.get(`${BASE_URL}/oashell-users`);
                    const formattedUsers = (uRes.data.data || []).map(u => ({
                        id: u.id,
                        email: u.email,
                        displayName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                        role: { roleName: u.roleName, canActAs: u.canActAs || [] },
                        roleId: u.roleId
                    }));
                    setWorkflowUsers(formattedUsers);
                }
            } catch (err) {
                console.error("Meta fetch error:", err);
            }
        };
        fetchMeta();
    }, [taskId]);

    const [auditLoading, setAuditLoading] = useState(false);
    const handleLoadAuditTrail = async () => {
        try {
            setAuditLoading(true);
            const res = await axios_tenant.get(`${BASE_URL}/tasks/${taskId}/audit`);
            setAudits(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch audit trail:", err);
        } finally {
            setAuditLoading(false);
        }
    };

    const [isAssigning, setIsAssigning] = useState(false);
    const handleAssign = async ({ processorId, qaId }) => {
        try {
            setIsAssigning(true);
            await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, { processorId, qaId });
            refetch();
        } catch (err) {
            console.error("Assignment failed:", err);
        } finally {
            setIsAssigning(false);
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleStatusChange = async (toStatus) => {
        try {
            setIsSubmitting(true);
            await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus });
            showSuccess(`Status changed to ${toStatus}`);
            refetch();
        } catch (err) {
            console.error("Status change failed:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'assign' | 'assignQa' | 'skipProcessing' | 'qcPending' | 'qaSubmit'

    // Resolve QA Name for message
    const displayQA = (() => {
        // Check local selection first (for Assign QA flow)
        const qaId = localQaId || (task && task.qaId);
        if (!qaId) return "Unassigned";
        const qa = workflowUsers.find(u => u.id === qaId);
        return qa ? qa.displayName : (task?.qaName || `ID: ${qaId}`);
    })();

    const handleConfirmedAction = async () => {
        try {
            setIsSubmitting(true);
            setConfirmOpen(false);

            if (confirmAction === 'assign') {
                // Admin/AM: assign Processor/QA from To Do (task stays in To Do)
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, {
                    processorId: localProcessorId,
                    qaId: localQaId || undefined
                }, { silent: true });
                showSuccess("Task assigned successfully");
            } else if (confirmAction === 'assignQa') {
                // Admin/AM: assign QA from Assign QA → then transition to QC Pending
                // Only call /assign if QA was actually changed (skip if already assigned via bulk)
                if (localQaId && localQaId !== task.qaId) {
                    await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, {
                        qaId: localQaId
                    }, { silent: true });
                }
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'QC Pending' }, { silent: true });
                showSuccess(`Task assigned to ${displayQA} and moved to QC Pending`);
            } else if (confirmAction === 'qcPending') {
                // Processor: In Progress → QC Pending (or Assign QA if no QA)
                const res = await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'QC Pending' }, { silent: true });
                const actualStatus = res.data?.data?.taskStatus;
                if (actualStatus === 'Assign QA' || !task.qaId) {
                    showSuccess("Task submitted — moved to \"Assign QA\" as no QA is assigned");
                } else {
                    showSuccess(`Task submitted for QC review`);
                }
            } else if (confirmAction === 'completeNoQc') {
                // COL-137: QA marked "Not Required" — processing completes the task
                await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'Completed' }, { silent: true });
                showSuccess("Task completed (QC not required)");
            } else if (confirmAction === 'qaSubmit') {
                // QA: Under Review → Completed (locks obs + snapshot)
                await axios_tenant.post(`${BASE_URL}/tasks/${taskId}/qa-submit`, undefined, { silent: true });
                showSuccess("Task completed successfully");
            } else if (confirmAction === 'skipProcessing') {
                // Admin/AM: Skip Processing → clear processor, assign QA, then move To Do → QC Pending (or Assign QA)
                if (localQaId) {
                    await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/assign`, {
                        processorId: null,
                        qaId: localQaId
                    }, { silent: true });
                    await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'QC Pending' }, { silent: true });
                    showSuccess("Processing skipped — task sent to QA");
                } else {
                    await axios_tenant.patch(`${BASE_URL}/tasks/${taskId}/status`, { toStatus: 'Assign QA' }, { silent: true });
                    showSuccess("Processing skipped — task moved to Assign QA");
                }
            }
            refetch();
        } catch (err) {
            console.error("Submit action failed:", err);
            const errMsg = err?.response?.data?.message || "Action failed. Please try again.";
            setSnackbar({ open: true, message: errMsg, severity: "error" });
        } finally {
            setIsSubmitting(false);
            setConfirmAction(null);
        }
    };

    const handlePrimarySubmit = () => {
        if (canAdminAssign && task.taskStatus === 'Assign QA') {
            // Admin/AM: Assign QA → QC Pending
            setConfirmAction('assignQa');
            setConfirmOpen(true);
        } else if (canAdminAssign && localProcessorId === 'SKIP_PROCESSING') {
            // Admin/AM: Skip Processing at To Do
            setConfirmAction('skipProcessing');
            setConfirmOpen(true);
        } else if (canAdminAssign) {
            // Admin/AM To Do: assign
            setConfirmAction('assign');
            setConfirmOpen(true);
        } else if (submitTransitions.includes('Completed') && task.qcRequired === false) {
            // COL-137: QA marked "Not Required" — processing completes the task directly
            setConfirmAction('completeNoQc');
            setConfirmOpen(true);
        } else if (submitTransitions.includes('QC Pending')) {
            // Processor In Progress → QC Pending
            setConfirmAction('qcPending');
            setConfirmOpen(true);
        } else if (submitTransitions.includes('Completed')) {
            // QA Under Review → Completed
            setConfirmAction('qaSubmit');
            setConfirmOpen(true);
        }
    };

    // Submit button enabled logic
    const hasPackages = packages && packages.length > 0;
    const isSubmitEnabled = isSubmitEnabledShared(task, hasPackages, userRole, localProcessorId, localQaId);
    const submitDisabledReason = getSubmitDisabledReasonShared(task, hasPackages, userRole, localProcessorId, localQaId);

    // Permission-driven flags
    const isAdminOrAM = userRole === "Admin" || userRole === "Application Manager";
    const canUpload = allowedActions.includes('upload');
    const canObserve = allowedActions.includes('observe');

    // Deadline Date (Admin/AM only) — OA Shell has no activity-based deadline
    // rule: a value comes from Rush or is set by hand, and the backend clears it
    // on un-rush. Show it whenever it is set.
    const saveDeadline = async (deadlineDate) => {
        try {
            await axios_tenant.put(`${BASE_URL}/tasks/${taskId}/admin-edit`, { deadlineDate }, { silent: true });
            showSuccess("Deadline updated");
            refetch();
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || "Failed to update deadline", severity: "error" });
        }
    };
    const deadlineValue = task?.deadlineDate || null;
    const deadlineFlag = (() => {
        if (!deadlineValue || task?.taskStatus === "Completed") return null;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const dl = new Date(`${String(deadlineValue).slice(0, 10)}T00:00:00`);
        if (isNaN(dl.getTime()) || dl > today) return null;
        return dl < today ? "Overdue" : "Due Today";
    })();
    const additionalFields = [
        {
            key: "deadlineDate", label: "Deadline Date",
            node: (
                <Box>
                    {isAdminOrAM ? (
                        <TextField
                            // Uncontrolled input: re-key on the value so a refetch
                            // (e.g. after toggling Rush) shows the new deadline.
                            key={`deadline-${deadlineValue || ""}`}
                            type="date" size="small"
                            defaultValue={deadlineValue ? String(deadlineValue).slice(0, 10) : ""}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: String(task?.createdAt || "").slice(0, 10) || undefined }}
                            onBlur={(e) => {
                                const v = e.target.value;
                                const current = deadlineValue ? String(deadlineValue).slice(0, 10) : "";
                                if (!v || v === current) return;
                                const createdDate = String(task?.createdAt || "").slice(0, 10);
                                if (createdDate && v < createdDate) {
                                    setSnackbar({ open: true, message: "Deadline Date cannot be earlier than the task creation date", severity: "error" });
                                    e.target.value = current;
                                    return;
                                }
                                saveDeadline(v);
                            }}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ color: deadlineFlag ? "#DC2626" : "#111827", fontWeight: deadlineFlag ? 700 : 400 }}>
                            {formatDateDDMMMYYYY(deadlineValue) || "—"}
                        </Typography>
                    )}
                    {deadlineFlag && (
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#DC2626", mt: 0.5 }}>{deadlineFlag}</Typography>
                    )}
                </Box>
            )
        }
    ];

    // Confirmation dialog message
    const getConfirmMessage = () => {
        let submitTarget = null;
        if (confirmAction === 'assign') submitTarget = "In Progress";
        else if (confirmAction === 'skipProcessing') submitTarget = localQaId ? "QC Pending" : "Assign QA";
        else if (confirmAction === 'assignQa') submitTarget = "QC Pending";
        else if (confirmAction === 'qcPending') submitTarget = task.qaId ? "QC Pending" : "Assign QA";
        else if (confirmAction === 'completeNoQc') return "QA is marked Not Required for this task. Submitting will mark it Completed without QC review. Continue?";
        else if (confirmAction === 'qaSubmit') submitTarget = "Completed";

        return getSubmitConfirmMessageShared(submitTarget, task, localQaId);
    };

    const [isUploading, setIsUploading] = useState(false);
    const handlePackageUpload = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".zip";
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("file", file);
            try {
                setIsUploading(true);
                await axios_tenant.post(`${BASE_URL}/tasks/${taskId}/package`, formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                await refetch();
                showSuccess("Package uploaded successfully");
            } catch (err) {
                console.error("Package upload failed:", err);
            } finally {
                setIsUploading(false);
            }
        };
        input.click();
    };

    const [downloadingVersion, setDownloadingVersion] = useState(null);
    const handlePackageDownload = async (version) => {
        try {
            setDownloadingVersion(version);
            const res = await axios_tenant.get(`${BASE_URL}/tasks/${taskId}/package/${version}`, { responseType: "blob" });

            // Extract filename from Content-Disposition header if available
            let fileName = `v${version}.zip`;
            const contentDisposition = res.headers['content-disposition'];
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                if (fileNameMatch?.[1]) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setDownloadingVersion(null);
        }
    };

    const [savingObservation, setSavingObservation] = useState(false);
    const handleObservationSave = async (obsData) => {
        try {
            setSavingObservation(true);
            await axios_tenant.post(`${BASE_URL}/tasks/${taskId}/observations`, obsData);
            await refetchObservations();
        } catch (err) {
            // COL-160: this used to console.error only, so a rejected save looked
            // like nothing happening at all — the validation error that blocked
            // every observation never reached the user, and the fault was
            // reported as a content problem rather than a failure to save.
            console.error("Observation save failed:", err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || "Failed to save observation",
                severity: "error"
            });
        } finally {
            setSavingObservation(false);
        }
    };

    const handleObservationDelete = async (observationId) => {
        try {
            await axios_tenant.delete(`${BASE_URL}/tasks/${taskId}/observations/${observationId}`, { silent: true });
            await refetchObservations();
        } catch (err) {
            const errMsg = err?.response?.data?.message || "Failed to delete observation.";
            setSnackbar({ open: true, message: errMsg, severity: "error" });
        }
    };

    const isPageBusy = isUploading || savingObservation || isSubmitting || isAssigning;


    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND_COLORS.PRIMARY }} /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ pt: 1, px: 4, pb: 10, position: 'relative' }} data-testid="oa-workflow-task-detail-page">
                {/* Details Title matched to screenshot exactly */}
                <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#1a1a2e', fontSize: '20px' }}>
                    Details
                </Typography>

                <Paper sx={{ borderRadius: 2, backgroundColor: '#fff', mb: 3 }}>
                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <TaskDetailPanel
                            task={task}
                            users={workflowUsers}
                            onAssign={canAdminAssign ? null : handleAssign}
                            onLocalAssign={canAdminAssign ? { setProcessorId: setLocalProcessorId, setQaId: setLocalQaId, processorId: localProcessorId, qaId: localQaId } : null}
                            onStatusChange={handleStatusChange}
                            permittedTransitions={permittedTransitions}
                            currentRole={userRole}
                            isAssigning={isAssigning}
                            additionalFields={additionalFields}
                        />

                        <Box>
                            <Typography fontWeight={600} sx={{ color: '#1a1a2e', fontSize: '16px', mb: 2 }}>
                                Package
                            </Typography>
                            <PackageSection
                                packages={packages}
                                canUpload={canUpload}
                                onUpload={handlePackageUpload}
                                onDownload={handlePackageDownload}
                                downloadingVersion={downloadingVersion}
                                isUploading={isUploading}
                                users={workflowUsers}
                            />
                        </Box>

                        <Box>
                            <Typography fontWeight={600} sx={{ color: '#1a1a2e', fontSize: '16px', mb: 2 }}>
                                Observations
                            </Typography>
                            <ObservationTable
                                observations={observations}
                                readOnly={!canObserve}
                                currentUser={currentUser}
                                currentRole={userRole}
                                onSave={handleObservationSave}
                                onDelete={handleObservationDelete}
                                isSaving={savingObservation}
                                users={workflowUsers}
                            />
                        </Box>

                        {/* Email Draft — shown for completed tasks for Admin/AM/QA (or delegated roles via canActAs) */}
                        {task?.taskStatus === "Completed" && (isAdminOrAM || userRole === "QA") && (
                            <EmailDraftSection taskId={taskId} />
                        )}

                        {/* Audit Trail Hidden per requirement */}
                        {/* (userRole === "Admin" || userRole === "Application Manager") && ( ... ) */}
                    </Box>

                    {/* Fixed Submit Button Footer — hidden when task is Completed */}
                    {task?.taskStatus !== "Completed" && (
                        <Box sx={{ p: 3, borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, zIndex: 10 }}>
                            {submitDisabledReason && (
                                <Typography variant="caption" sx={{ color: '#EF4444', fontStyle: 'italic' }}>
                                    {submitDisabledReason}
                                </Typography>
                            )}
                            <Button
                                variant="primary"
                                onClick={handlePrimarySubmit}
                                disabled={!isSubmitEnabled || isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : "Submit"}
                            </Button>
                        </Box>
                    )}
                </Paper>

                {/* Universal Confirmation Modal */}
                <Dialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, minWidth: 400, p: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#1a1a2e', pb: 1 }}>
                        Confirm Submission
                    </DialogTitle>
                    <DialogContent sx={{ pb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6 }}>
                            {getConfirmMessage()}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
                            No
                        </Button>
                        <Button variant="primary" onClick={handleConfirmedAction} disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : "Yes"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbar.severity}
                        sx={{ width: '100%', boxShadow: 3 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <Backdrop
                    open={isPageBusy}
                    sx={{
                        position: 'absolute',
                        zIndex: (theme) => theme.zIndex.modal - 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: 2
                    }}
                >
                    <CircularProgress size={40} sx={{ color: BRAND_COLORS.PRIMARY }} />
                </Backdrop>
            </Box>
        </ThemeProvider>
    );
};

export default OAWorkflowTaskDetail;
